import { FC, ReactNode } from 'react';

import { Input, InputNumber, Select, Space } from 'antd';

/** ID — всё значение метки целиком (в БД utm_mask = NULL) */
export const UTM_MASK_MODE_SINGLE = 0;
/** ID — N-й параметр значения метки, параметры разделены разделителем */
export const UTM_MASK_MODE_POSITION = 1;
/** ID лежит внутри параметра, между символами «перед» и «после» */
export const UTM_MASK_MODE_INSIDE = 2;

export interface IUtmMaskValue {
  mode: number;
  number: number | null;
  divider: string;
  before: string;
  after: string;
}

/** Тело запроса /integration/set-utm-mask. value оставлен для совместимости со старым бэкендом */
export interface IUtmMaskPayload extends IUtmMaskValue {
  value: boolean;
}

export const getEmptyUtmMask = (
  mode: number = UTM_MASK_MODE_SINGLE
): IUtmMaskValue => ({
  mode,
  number: null,
  divider: '',
  before: '',
  after: '',
});

/**
 * Ячейка приходит от бэкенда как { mode, number, divider, before, after } либо null.
 * Старый бэкенд отдаёт только { number, divider } — это режим «по счёту».
 */
export const normalizeUtmMask = (cellData: any): IUtmMaskValue => {
  if (!cellData) {
    return getEmptyUtmMask();
  }

  return {
    mode: Number(cellData.mode) || UTM_MASK_MODE_POSITION,
    number: cellData.number ?? null,
    divider: cellData.divider ?? '',
    before: cellData.before ?? '',
    after: cellData.after ?? '',
  };
};

export const toUtmMaskPayload = (mask: IUtmMaskValue): IUtmMaskPayload => ({
  ...mask,
  value: mask.mode > UTM_MASK_MODE_SINGLE,
});

/**
 * Ширина каждого поля. Поле равно ширине своей подписи, иначе строка выглядит рваной:
 * подпись задаёт ширину столбика, а инпут жмётся влево под ней.
 */
const FIELD_WIDTH = {
  // Самый длинный пункт — «Несколько параметров в метке», он должен помещаться целиком
  mode: 230,
  number: 115,
  divider: 100,
  marker: 135,
};
const FIELD_GAP = 8;

/** Ширина столбца «Где в UTM метке ID объявления?» — зависит от самого широкого режима в таблице */
export const getUtmMaskColumnWidth = (
  masks: Record<string, IUtmMaskValue>
): number => {
  const modes = Object.values(masks).map((mask) => mask.mode);
  // Поля плюс промежутки между ними и отступы ячейки таблицы
  const padding = 40;
  const positionWidth =
    FIELD_WIDTH.mode + FIELD_WIDTH.number + FIELD_WIDTH.divider + (FIELD_GAP * 2);
  const markersWidth = (FIELD_WIDTH.marker + FIELD_GAP) * 2;

  if (modes.some((mode) => mode === UTM_MASK_MODE_INSIDE)) {
    return positionWidth + markersWidth + padding;
  }
  if (modes.length > 0 && modes.every((mode) => mode === UTM_MASK_MODE_POSITION)) {
    return positionWidth + padding;
  }

  return FIELD_WIDTH.mode + padding;
};

interface IFieldProps {
  /** Пусто — подпись-распорка: поле без названия встаёт на одну линию с остальными */
  label?: string;
  hint?: string;
  width: number;
  children: ReactNode;
}

const Field: FC<IFieldProps> = ({ label, hint, width, children }) => (
  <div
    style={{
      width: `${width}px`,
      whiteSpace: 'nowrap',
      // Отступ был у прежней вёрстки — сохраняет положение блока по высоте строки таблицы
      paddingBottom: '21px',
    }}
  >
    <label
      title={hint}
      style={{ display: 'block', lineHeight: '22px', marginBottom: '4px' }}
    >
      {label || ' '}
    </label>
    {children}
  </div>
);

interface IUtmMaskCellProps {
  value: IUtmMaskValue;
  onChange: (next: IUtmMaskValue) => void;
}

/**
 * Настройка места, где внутри UTM метки лежит ID объявления.
 * Используется в таблицах кабинетов на /source/ads (ConnectAds и TableCabs).
 */
const UtmMaskCell: FC<IUtmMaskCellProps> = ({ value, onChange }) => {
  const isPacked = value.mode !== UTM_MASK_MODE_SINGLE;
  const isInside = value.mode === UTM_MASK_MODE_INSIDE;
  // В режиме «внутри параметра» номер не обязателен: параметр можно найти по символам перед ID
  const isNumberRequired = value.mode === UTM_MASK_MODE_POSITION;
  const isNumberMissing = isNumberRequired && !value.number;
  // В режиме «внутри параметра» пустой разделитель допустим: вся метка считается одним параметром
  const isDividerMissing = value.mode === UTM_MASK_MODE_POSITION && !value.divider;
  const isMarkersMissing = isInside && !value.before && !value.after;

  return (
    <Space size={FIELD_GAP} align={'start'}>
      <Field width={FIELD_WIDTH.mode}>
        <Select
          style={{ width: '100%' }}
          value={value.mode}
          onChange={(mode) => onChange({ ...value, mode })}
        >
          <Select.Option value={UTM_MASK_MODE_SINGLE}>
            Одно значение в метке
          </Select.Option>
          <Select.Option value={UTM_MASK_MODE_POSITION}>
            Несколько параметров в метке
          </Select.Option>
          <Select.Option value={UTM_MASK_MODE_INSIDE}>
            ID внутри параметра
          </Select.Option>
        </Select>
      </Field>
      {isPacked && (
        <>
          <Field
            width={FIELD_WIDTH.number}
            label={'Какой по счёту?'}
            hint={
              isInside
                ? 'Можно оставить пустым — тогда ID ищется по символам перед ним в любом месте метки. Так надёжнее, если порядок параметров меняется'
                : undefined
            }
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              precision={0}
              status={isNumberMissing ? 'error' : ''}
              placeholder={isInside ? 'авто' : ''}
              value={value.number}
              onChange={(number) => {
                const next =
                  number === null || number === undefined
                    ? null
                    : parseInt(number.toString(), 10);

                if (next !== null && (isNaN(next) || next < 1)) return;

                onChange({ ...value, number: next });
              }}
            />
          </Field>
          <Field width={FIELD_WIDTH.divider} label={'Разделитель'}>
            <Input
              style={{ width: '100%', fontWeight: 600 }}
              status={isDividerMissing ? 'error' : ''}
              value={value.divider}
              onChange={(event) =>
                onChange({ ...value, divider: event.target.value })
              }
            />
          </Field>
        </>
      )}
      {isInside && (
        <>
          <Field
            width={FIELD_WIDTH.marker}
            label={'Символы перед ID'}
            hint={'Например, «ad_id:» — всё, что стоит в метке прямо перед номером объявления'}
          >
            <Input
              style={{ width: '100%' }}
              status={isMarkersMissing ? 'error' : ''}
              placeholder={'ad_id:'}
              value={value.before}
              onChange={(event) =>
                onChange({ ...value, before: event.target.value })
              }
            />
          </Field>
          <Field
            width={FIELD_WIDTH.marker}
            label={'Символы после ID'}
            hint={'Необязательно: если не заполнить, ID заканчивается на разделителе'}
          >
            <Input
              style={{ width: '100%' }}
              placeholder={'до разделителя'}
              value={value.after}
              onChange={(event) =>
                onChange({ ...value, after: event.target.value })
              }
            />
          </Field>
        </>
      )}
    </Space>
  );
};

export default UtmMaskCell;
